import React, { useMemo } from "react";
import { getOrderedCricketPlayerImageUrls } from "../../../utils/cricketPlayerImageResolve";
import { useResolvedImageSrc } from "../../../utils/useResolvedImageSrc";

/**
 * Player headshot: prefer `imageUrl` from the feed; otherwise flat S3
 * `Cricket/Player Images/{slug}.png`, then legacy `IPL+Team+Images/{team}/{player}.png`.
 *
 * Prefers a session-cached URL (or the first candidate optimistically), then confirms
 * off-DOM so missing assets never flash the browser broken-image icon or the placeholder.
 *
 * @param {string} [backdropColor] — e.g. rgba(...) tile behind the photo (Bullion slip / dark UI).
 * @param {string} [imgMixBlendMode] — e.g. `multiply` to soften solid white backdrops against `backdropColor`.
 * @param {React.ReactNode} [fallback] — shown instead of the generic silhouette when no headshot exists.
 */
export const PlayerHeadshotImage = ({
  teamName = "",
  playerName = "",
  imageUrl = "",
  height = "40px",
  width = "40px",
  backdropColor = "",
  imgMixBlendMode = "",
  fallback = null,
}) => {
  // Keyed by value: callers often pass a fresh array (or null) each render, which would restart the lookup and keep the image hidden.
  const teamsKey = (Array.isArray(teamName) ? teamName : teamName ? [teamName] : []).join("\u0000");
  const teams = useMemo(() => (teamsKey ? teamsKey.split("\u0000") : []), [teamsKey]);
  const safePlayer = (playerName || "").toString().trim();
  const fallbackImageUrl = `${process.env.CDN_ENDPOINT}/player-placeholder-dark.svg`;
  const feedUrl = (imageUrl && typeof imageUrl === "string" && imageUrl.trim()) ? imageUrl.trim() : "";
  const orderedUrls = useMemo(
    () => getOrderedCricketPlayerImageUrls(safePlayer, teams),
    [safePlayer, teams],
  );
  const candidateUrls = useMemo(() => {
    const urls = [];
    if (feedUrl) urls.push(feedUrl);
    for (const url of orderedUrls) {
      if (url && !urls.includes(url)) urls.push(url);
    }
    return urls;
  }, [feedUrl, orderedUrls]);

  const { src, hidden, onError } = useResolvedImageSrc(candidateUrls, fallbackImageUrl);

  function handleLoad(event) {
    const img = event.target;
    if (img.naturalHeight > img.naturalWidth) {
      img.style.maxHeight = height;
    }
  }

  const teamForAlt = teams[0] || "";
  const altText = safePlayer ? `${safePlayer}${teamForAlt ? ` (${teamForAlt})` : ""}` : "Player";

  const backdrop = String(backdropColor || "").trim();
  const blend = String(imgMixBlendMode || "").trim();
  const bullionTile = Boolean(backdrop || blend);

  if (fallback && src === fallbackImageUrl) {
    return <div style={{ width, height, minWidth: width, minHeight: height }}>{fallback}</div>;
  }

  return (
    <div
      style={{
        width,
        height,
        minWidth: width,
        minHeight: height,
        ...(backdrop ? { backgroundColor: backdrop, isolation: "isolate" } : {}),
      }}
    >
      <img
        src={src}
        onError={onError}
        onLoad={handleLoad}
        alt={altText}
        style={{
          width,
          height,
          maxWidth: width,
          maxHeight: height,
          ...(bullionTile
            ? {
                objectFit: "contain",
                display: "block",
                backgroundColor: "transparent",
                ...(blend ? { mixBlendMode: blend } : {}),
              }
            : {}),
          ...(hidden ? { visibility: "hidden" } : {}),
        }}
        decoding="async"
      />
    </div>
  );
};

/** @deprecated Use `PlayerHeadshotImage` — name kept for older imports. */
/** @deprecated Use {@link TennisPlayerImage} from `./TennisPlayerImage`. */
export const TennisPlayerImage = PlayerHeadshotImage;

export default PlayerHeadshotImage;
